import React, { useCallback, useContext, useEffect, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';

import { DomainObject, Params, Result } from 'types';
import { PageKey, SortOrder } from 'enums';
import { Table } from 'components/table/table';
import {
  BreadcrumbsCtx,
  initialBreadcrumbs,
} from 'components/portfolio/breadcrumbs-ctx';
import {
  AdditionalRowComponentProps,
  TableColumn,
} from 'components/table/table-types';
import { LentTableRefreshCtx } from './lent-table-refresh-ctx';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    table: {
      margin: theme.spacing(2),
    },
    error: {
      fontWeight: 700,
      marginLeft: 20,
    },
    historyDetailField: {
      display: 'flex',
      padding: '2px 5px',
      alignItems: 'center',
    },
    historyDetailFieldLabel: {
      width: 110,
      fontWeight: 500,
      textAlign: 'right',
      marginRight: 20,
    },
    wrapper: {
      backgroundColor: '#FAFAFA',
      minHeight: 100,
      padding: '0px 20px',
    },
  })
);

interface LentTableProps<T> {
  title: string;
  pageKey: PageKey;
  columns: TableColumn[];
  params?: Params;
  fetchFunction: (params: Params) => Promise<Result<T>>;

  DetailComponent?: (props: AdditionalRowComponentProps<T>) => JSX.Element;
}

export function LentTable<T extends DomainObject>({
  pageKey,
  title,
  columns,
  params,
  fetchFunction,
  DetailComponent,
}: LentTableProps<T>) {
  const classes = useStyles();

  const { setBreadcrumbs } = useContext(BreadcrumbsCtx);
  const {
    field: defaultOrderBy = 'created',
    order: defaultOrder = SortOrder.DESC,
  } = params?.sort?.[0] ?? {};

  const [order, setOrder] = useState<SortOrder>(defaultOrder);
  const [orderBy, setOrderBy] = useState<string>(defaultOrderBy);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [rows, setRows] = useState<T[]>([]);
  const [count, setCount] = useState(0);
  const [searching, setSearching] = useState(false);

  const getTableItems = useCallback(async () => {
    const requestParams: Params = {
      ...params,
      sort: [{ field: orderBy, order }],
      offset: page * rowsPerPage,
      size: rowsPerPage,
    };

    try {
      setSearching(true);
      const { items, count } = await fetchFunction(requestParams);

      if (items && count !== undefined) {
        setCount(count);
        setRows(items);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setSearching(false);
    }
  }, [fetchFunction, order, orderBy, page, params, rowsPerPage]);

  const refreshTablePage = useCallback(async () => {
    await getTableItems();
  }, [getTableItems]);

  useEffect(() => {
    void getTableItems();
  }, [getTableItems]);

  useEffect(() => {
    setBreadcrumbs([
      ...initialBreadcrumbs.breadcrumbs,
      { key: pageKey, title },
    ]);
  }, [pageKey, setBreadcrumbs, title]);

  return (
    <LentTableRefreshCtx.Provider value={{ refreshTablePage }}>
      <Grid container>
        <Grid item xs={12} className={classes.table}>
          <Table<T>
            rows={rows}
            setRows={setRows}
            count={count}
            setCount={setCount}
            order={order}
            setOrder={setOrder}
            orderBy={orderBy}
            setOrderBy={setOrderBy}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            searching={searching}
            columns={columns}
            expandableRows={!!DetailComponent}
            AdditionalRowComponent={DetailComponent}
          />
        </Grid>
      </Grid>
    </LentTableRefreshCtx.Provider>
  );
}
